import { Injectable, computed, inject, signal } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { HttpBaseService } from './http-base.service';
import { StorageService } from './storage.service';

export interface FirmSummary {
  _id: string;
  name: string;
  parentFirmId?: string | null;
}

export interface FirmFamily {
  root: FirmSummary | null;
  children: FirmSummary[];
}

/** Per-session key: a switcher choice must not outlive the session (MP-5643 item 2). */
const SELECTED_CHILD_KEY = 'mp5636.actingChildFirmId';

/**
 * MP-5643 — the FM's acting child-firm context.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS SOLVES
 * ---------------------------------------------------------------------------
 *
 * A reviewer's identity is keyed to the family ROOT, so nothing about the
 * reviewer can tell you which CHILD an FM means. The acting firm comes from
 * the ACTOR, and a parent-level FM has no child of their own - their
 * `profile.firm` IS the root. Without an explicit choice there is simply no
 * child id anywhere in the request, which is why the child-scoped features
 * (MP-5734's FirmProfileEdit, MP-5736's profile edits) are inert for FMs
 * until this exists.
 *
 * This service is that choice, held in one place so that every consumer -
 * the interceptor, `UserService.getActingChildFirmId()`, the switcher UI -
 * reads the same value.
 *
 * ---------------------------------------------------------------------------
 * HOW THE CHOICE REACHES THE SERVER
 * ---------------------------------------------------------------------------
 *
 * Through the EXISTING `X-Tenant-Id` header, not a new parameter.
 *
 * That header carries the ACTING firm (see `token.interceptor.ts`), and the
 * server derives the family root itself via `middlewares/resolveTenantRoot`.
 * So selecting a child here is the whole mechanism: work items key to that
 * child, identity still resolves up to the root, and no call site needs to
 * learn a new convention.
 *
 * NOTE this is the OPPOSITE of what MP-5643's description says ("the
 * `X-Tenant-Id` header always carries the family root"). That premise was
 * disproved - the frontend has always sent the acting firm and both auth
 * middlewares copy the claim through unresolved. Implementing it as written
 * would re-attribute every PM-created work item to the parent, because
 * `endpoints/jobs.js` sets a job posting's owning firm straight from this
 * header. See `docs/MP-5636-HEADER-TENANT-AUDIT.md`.
 *
 * ---------------------------------------------------------------------------
 * STANDALONE FIRMS SEE NOTHING, BY CONSTRUCTION
 * ---------------------------------------------------------------------------
 *
 * `GET firms/family` answers with an EMPTY `children` array for a childless
 * firm - which is every Staftr and PWC firm. `hasSwitcher` is then false, the
 * dropdown does not render, `selectedChildId` stays '' and the interceptor
 * keeps sending exactly what it sends today.
 */
@Injectable({ providedIn: 'root' })
export class FirmContextService {

  private readonly http = inject(HttpBaseService);
  private readonly storage = inject(StorageService);

  /** The family root, as the server reports it. */
  readonly rootFirm = signal<FirmSummary | null>(null);

  /** Child firms of the family; empty for every standalone firm. */
  readonly children = signal<FirmSummary[]>([]);

  /** The acting child, or '' when the caller is acting at the root. */
  readonly selectedChildId = signal<string>(this.readStoredSelection());

  /** Only a family with children gets a switcher. */
  readonly hasSwitcher = computed(() => this.children().length > 0);

  /** The acting child's name, for the "added by <firm>" marker (MP-5736). */
  readonly selectedChildName = computed(() => {
    const id = this.selectedChildId();

    return id ? (this.children().find((child) => child._id === id)?.name || '') : '';
  });

  /**
   * Load the caller's family and settle on an acting child.
   *
   * Safe to call for anyone: a reviewer or a standalone firm gets an empty
   * `children` array and nothing changes. Never rejects - a failure here must
   * not block the shell from rendering, and falling back to "no child
   * context" is exactly today's behaviour.
   */
  async loadFamily(): Promise<void> {
    try {
      const family: FirmFamily = await lastValueFrom(this.http.get('firms/family'));

      this.rootFirm.set(family?.root || null);
      this.children.set(family?.children || []);
      this.settleSelection();
    } catch {
      this.rootFirm.set(null);
      this.children.set([]);
      this.selectedChildId.set('');
    }
  }

  /**
   * Switch the acting child. Ignores an id outside the loaded family rather
   * than trusting it - the server validates too, but a bad local value would
   * send every subsequent request with a tenant header the caller cannot act
   * on, which reads as a wave of 403s rather than as a bad selection.
   */
  select(childFirmId: string): void {
    if (!this.children().some((child) => child._id === childFirmId)) {
      return;
    }

    this.selectedChildId.set(childFirmId);
    this.storage.setLocalStorageItem(SELECTED_CHILD_KEY, childFirmId);
  }

  /** Clear on logout, so the next user in this browser starts clean. */
  clear(): void {
    this.rootFirm.set(null);
    this.children.set([]);
    this.selectedChildId.set('');
    this.storage.setLocalStorageItem(SELECTED_CHILD_KEY, '');
  }

  /**
   * Keep the stored choice when it is still a child of this family, otherwise
   * fall back to the first child (MP-5643 item 2: "first child or last used").
   * A family with no children settles on '' - no child context at all.
   */
  private settleSelection(): void {
    const children = this.children();

    if (!children.length) {
      this.selectedChildId.set('');

      return;
    }

    const stored = this.readStoredSelection();
    const keep = children.some((child) => child._id === stored) ? stored : children[0]._id;

    this.selectedChildId.set(keep);
    this.storage.setLocalStorageItem(SELECTED_CHILD_KEY, keep);
  }

  private readStoredSelection(): string {
    try {
      return this.storage.getLocalStorageItem(SELECTED_CHILD_KEY) || '';
    } catch {
      return '';
    }
  }
}
